export type Locale = "de" | "en";

export const LOCALES: readonly Locale[] = ["de", "en"] as const;
export const DEFAULT_LOCALE: Locale = "de";

export const OG_LOCALES: Record<Locale, string> = {
  de: "de_DE",
  en: "en_US",
};

export const HTML_LANGS: Record<Locale, string> = {
  de: "de",
  en: "en",
};

/**
 * Blog URL prefixes per locale.
 * DE posts live at /blog/{slug}, EN posts live at /en/blog/{slug}.
 */
export function blogPostUrl(slug: string, locale: Locale): string {
  return locale === "en" ? `/en/blog/${slug}` : `/blog/${slug}`;
}

export function getLocaleFromPathname(pathname: string): Locale {
  return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "de";
}

/** Strip a leading `/en` segment, returning the DE-equivalent path. */
function stripLocalePrefix(pathname: string): string {
  if (pathname === "/en") return "/";
  return pathname.replace(/^\/en(?=\/)/, "") || "/";
}

/**
 * Compute the URL of the current page in the requested locale.
 *
 * EN target behavior is intentionally strict: every DE page switches to `/en`.
 * This matches the product requirement that EN should always open the English
 * landing page rather than mirroring arbitrary DE paths.
 */
export function getAlternateUrl(pathname: string, targetLocale: Locale): string {
  if (getLocaleFromPathname(pathname) === targetLocale) return pathname;

  const dePath = stripLocalePrefix(pathname);

  if (targetLocale === "de") {
    // Product requirement: switching DE from EN landing should open DE blog.
    if (pathname === "/en") return "/blog";
    // EN → DE. Post slugs differ per language → fall back to the DE blog index.
    if (dePath.startsWith("/blog/")) return "/blog";
    return dePath;
  }

  // DE → EN always lands on English home by requirement.
  void dePath;
  return "/en";
}
